/**
 * Step 1 — Basics (PRD §7.2): title, department, country/city, workplace,
 * employment type, seniority. Country is required unless fully remote; city is
 * required for on-site/hybrid.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import type { TextInput } from '@/components/common/AppText';
import { useTranslation } from 'react-i18next';
import { spacing } from '@tabbio/tokens';
import { NativeForm } from '../../common/NativeForm';
import {
  COUNTRY_OPTIONS,
  DEPARTMENT_OPTIONS,
  EMPLOYMENT_OPTIONS,
  WORK_TYPE_OPTIONS,
  getCitiesForCountryOption,
  getCountryForCityOption,
  isCityOutsideCountry,
  type OptionItem,
} from '../post-job-flow.data';
import { SENIORITY_VALUES, isKnownOptionId } from './post-job-machine';
import {
  FlowChips,
  FlowDropdown,
  FlowTextField,
  fieldStyles,
  type FlowOption,
} from './post-job-ui';
import type { PostJobFlowController } from './use-post-job-flow';

function useCommonOptions(options: OptionItem[]): FlowOption[] {
  const { t } = useTranslation('common');
  return useMemo(
    () =>
      options.map((option) => ({
        value: option.value,
        label: t(option.labelKey as never) as string,
      })),
    [options, t],
  );
}

/**
 * Keeps a stored value the picker does not know about visible and selected.
 *
 * Jobs created outside this wizard carry free-text departments/locations
 * ("People", "Growth"). Rendering the placeholder for them read as "empty" and
 * invited the employer to overwrite real data; appending the stored value as
 * its own option shows the truth and leaves it round-trippable.
 */
function useOptionsWithStoredValue(
  options: FlowOption[],
  value: string,
  source: OptionItem[],
): FlowOption[] {
  return useMemo(() => {
    const trimmed = value.trim();
    if (!trimmed || isKnownOptionId(trimmed, source)) return options;
    return [...options, { value: trimmed, label: trimmed }];
  }, [options, source, value]);
}

export function PostJobBasicsStep({ flow }: { flow: PostJobFlowController }) {
  const { t } = useTranslation('common');
  const { t: tEmployer } = useTranslation('employer');
  const titleRef = useRef<TextInput>(null);

  const { state, setBasics, errorFor, focusFieldId, clearFocusField } = flow;

  // Cities are scoped to the selected country so a UAE role cannot be filed
  // under Riyadh; with no country picked yet the whole catalogue stays open.
  const citySource = useMemo(
    () => getCitiesForCountryOption(state.basics.country),
    [state.basics.country],
  );

  const departmentBaseOptions = useCommonOptions(DEPARTMENT_OPTIONS);
  const countryBaseOptions = useCommonOptions(COUNTRY_OPTIONS);
  const cityBaseOptions = useCommonOptions(citySource);
  const departmentOptions = useOptionsWithStoredValue(
    departmentBaseOptions,
    state.basics.department,
    DEPARTMENT_OPTIONS,
  );
  const countryOptions = useOptionsWithStoredValue(
    countryBaseOptions,
    state.basics.country,
    COUNTRY_OPTIONS,
  );
  const cityOptions = useOptionsWithStoredValue(
    cityBaseOptions,
    state.basics.city,
    citySource,
  );
  const workTypeOptions = useCommonOptions(WORK_TYPE_OPTIONS) as FlowOption<
    'onsite' | 'hybrid' | 'remote'
  >[];
  const employmentOptions = useCommonOptions(EMPLOYMENT_OPTIONS) as FlowOption<
    'full_time' | 'part_time' | 'contract' | 'internship'
  >[];
  const seniorityOptions = useMemo(
    () =>
      SENIORITY_VALUES.map((value) => ({
        value,
        label: tEmployer(
          `jobFlow.options.seniority.${value}` as never,
        ) as string,
      })),
    [tEmployer],
  );

  useEffect(() => {
    if (focusFieldId === 'title') {
      titleRef.current?.focus();
      clearFocusField();
    }
  }, [clearFocusField, focusFieldId]);

  // Switching country clears a city that belongs somewhere else, so the field
  // never keeps a stale pairing the filtered list can no longer show. A
  // free-text city (from an imported job) is left alone.
  const handleCountryChange = useCallback(
    (country: string) => {
      setBasics(
        isCityOutsideCountry(state.basics.city, country)
          ? { country, city: '' }
          : { country },
      );
    },
    [setBasics, state.basics.city],
  );

  // Picking a city before a country fills the country in rather than leaving a
  // required field the employer has effectively already answered.
  const handleCityChange = useCallback(
    (city: string) => {
      const owner = getCountryForCityOption(city);
      setBasics(
        !state.basics.country && owner ? { city, country: owner } : { city },
      );
    },
    [setBasics, state.basics.country],
  );

  const isRemote = state.basics.workMode === 'remote';

  return (
    <NativeForm style={styles.wrap}>
      <FlowTextField
        ref={titleRef}
        label={t('jobPost.step2.fields.title')}
        value={state.basics.title}
        onChangeText={(title) => setBasics({ title })}
        placeholder={t('jobPost.step2.placeholders.title')}
        errorKey={errorFor('title')}
      />

      <FlowDropdown
        label={t('jobPost.step2.fields.department')}
        value={state.basics.department}
        placeholder={t('jobPost.step2.placeholders.department')}
        options={departmentOptions}
        onChange={(department) => setBasics({ department })}
      />

      <FlowChips
        label={t('jobPost.step2.fields.workType')}
        value={state.basics.workMode}
        options={workTypeOptions}
        onChange={(workMode) => setBasics({ workMode })}
      />

      <View style={fieldStyles.twoColumn}>
        <View style={fieldStyles.flexOne}>
          <FlowDropdown
            label={
              isRemote
                ? (tEmployer('jobFlow.fields.countryOptional') as string)
                : t('jobPost.step2.fields.country')
            }
            value={state.basics.country}
            placeholder={t('jobPost.step2.placeholders.country')}
            options={countryOptions}
            onChange={handleCountryChange}
            errorKey={errorFor('country')}
          />
        </View>
        <View style={fieldStyles.flexOne}>
          <FlowDropdown
            label={
              isRemote
                ? (tEmployer('jobFlow.fields.cityOptional') as string)
                : t('jobPost.step2.fields.city')
            }
            value={state.basics.city}
            placeholder={t('jobPost.step2.placeholders.city')}
            options={cityOptions}
            onChange={handleCityChange}
            errorKey={errorFor('city')}
          />
        </View>
      </View>

      <FlowChips
        label={t('jobPost.step2.fields.employment')}
        value={state.basics.employment}
        options={employmentOptions}
        onChange={(employment) => setBasics({ employment })}
      />

      <FlowDropdown
        label={tEmployer('jobFlow.fields.seniority') as string}
        value={state.basics.seniority}
        placeholder={t('jobPost.step2.placeholders.experienceLevel')}
        options={seniorityOptions}
        onChange={(seniority) => setBasics({ seniority })}
      />
    </NativeForm>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing[6],
  },
});
